// Per-screen tuning. Lists every navigable screen grouped by module and
// lets the admin toggle visibility, pick which list columns appear, pick
// which row actions are exposed. Read by mobile via useScreenConfig().

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Input } from '@erp/ui';
import { ChevronDown, ChevronRight, Eye, EyeOff } from '@/components/icons';
import {
  SCREEN_CATALOG, type ScreenMeta, type ScreenConfig, type ModulesFlags,
} from '@erp/app-builder';

interface PagesTabProps {
  value: Record<string, ScreenConfig>;
  onChange: (next: Record<string, ScreenConfig>) => void;
  /** Used to grey out screens whose module is disabled — they're still
   *  configurable for the day the admin re-enables the module. */
  enabledModules: ModulesFlags;
}

const DEFAULT_CFG: ScreenConfig = {
  visible: true,
  showStats: true,
  columns: undefined,
  actions: undefined,
  defaultFilters: undefined,
};

export function PagesTab({ value, onChange, enabledModules }: PagesTabProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [openModules, setOpenModules] = useState<Set<string>>(() => new Set(['sales']));

  // Group screens by module, optionally filtered by name match.
  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const g: Record<string, ScreenMeta[]> = {};
    for (const s of SCREEN_CATALOG) {
      if (!s.navigable) continue;
      if (needle && !s.id.toLowerCase().includes(needle)) continue;
      (g[s.module] ??= []).push(s);
    }
    return g;
  }, [filter]);

  const moduleOrder = [
    'sales', 'caisse', 'purchases', 'stock', 'payments', 'treasury',
    'checks', 'withholding', 'storefront', 'core', 'settings',
  ].filter((m) => grouped[m] && grouped[m].length > 0);

  function get(id: string): ScreenConfig {
    return value[id] ?? DEFAULT_CFG;
  }
  function set(id: string, patch: Partial<ScreenConfig>) {
    onChange({ ...value, [id]: { ...get(id), ...patch } });
  }
  function reset(id: string) {
    const next = { ...value };
    delete next[id];
    onChange(next);
  }

  function toggleSection(m: string) {
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-muted-foreground">
        {t('appBuilder.pages.help', {
          defaultValue: 'Choisissez quels écrans sont visibles sur le mobile et quelles colonnes / actions apparaissent dans les listes.',
        })}
      </p>

      <Input
        placeholder={t('appBuilder.pages.search', { defaultValue: 'Filtrer…' })}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      <div className="space-y-3">
        {moduleOrder.map((m) => {
          const items = grouped[m] ?? [];
          const open = openModules.has(m);
          const moduleEnabled = m === 'core' || m === 'settings' || (enabledModules as Record<string, boolean>)[m] === true;
          return (
            <Card key={m} className={moduleEnabled ? '' : 'opacity-60'}>
              <CardContent className="py-3 px-3">
                <button
                  type="button"
                  onClick={() => toggleSection(m)}
                  className="flex items-center gap-2 w-full text-left"
                >
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="font-semibold text-sm">{moduleLabel(m)}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {items.length} {t('appBuilder.pages.screens', { defaultValue: 'écrans' })}
                  </span>
                  {!moduleEnabled ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                      {t('appBuilder.pages.moduleDisabled', { defaultValue: 'Module désactivé' })}
                    </span>
                  ) : null}
                </button>

                {open ? (
                  <div className="mt-3 space-y-2">
                    {items.map((s) => (
                      <ScreenRow
                        key={s.id}
                        screen={s}
                        cfg={get(s.id)}
                        onPatch={(patch) => set(s.id, patch)}
                        onReset={() => reset(s.id)}
                      />
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ScreenRow({
  screen,
  cfg,
  onPatch,
  onReset,
}: {
  screen: ScreenMeta;
  cfg: ScreenConfig;
  onPatch: (patch: Partial<ScreenConfig>) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visibleColumns = cfg.columns ?? screen.columns ?? [];
  const visibleActions = cfg.actions ?? screen.actions ?? [];

  return (
    <div className="rounded border border-input p-2 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPatch({ visible: !cfg.visible })}
          className={`flex h-7 w-7 items-center justify-center rounded ${
            cfg.visible ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}
          aria-label={cfg.visible ? 'Hide' : 'Show'}
        >
          {cfg.visible ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 text-left"
        >
          <div className="text-sm font-medium">{screen.id}</div>
          <div className="text-xs text-muted-foreground">{screen.view}</div>
        </button>
        <Button size="sm" variant="ghost" onClick={onReset}>
          {t('common.reset', { defaultValue: 'Reset' })}
        </Button>
      </div>

      {expanded ? (
        <div className="pl-9 pr-2 space-y-3 pt-2 border-t border-input">
          {/* Show stats toggle */}
          {(screen.view === 'list' || screen.view === 'dashboard') ? (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={cfg.showStats !== false}
                onChange={(e) => onPatch({ showStats: e.target.checked })}
              />
              {t('appBuilder.pages.showStats', { defaultValue: 'Afficher la zone statistiques en haut' })}
            </label>
          ) : null}

          {/* Columns picker */}
          {(screen.columns ?? []).length > 0 ? (
            <div>
              <div className="text-xs font-semibold mb-1.5">
                {t('appBuilder.pages.columns', { defaultValue: 'Colonnes visibles' })}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(screen.columns ?? []).map((col) => {
                  const on = visibleColumns.includes(col);
                  return (
                    <button
                      key={col}
                      type="button"
                      onClick={() => {
                        const next = on
                          ? visibleColumns.filter((c) => c !== col)
                          : [...visibleColumns, col];
                        onPatch({ columns: next });
                      }}
                      className={`text-xs px-2 py-1 rounded border ${
                        on
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-input'
                      }`}
                    >
                      {col}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Actions picker */}
          {(screen.actions ?? []).length > 0 ? (
            <div>
              <div className="text-xs font-semibold mb-1.5">
                {t('appBuilder.pages.actions', { defaultValue: 'Actions exposées' })}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(screen.actions ?? []).map((act) => {
                  const on = visibleActions.includes(act);
                  return (
                    <button
                      key={act}
                      type="button"
                      onClick={() => {
                        const next = on
                          ? visibleActions.filter((a) => a !== act)
                          : [...visibleActions, act];
                        onPatch({ actions: next });
                      }}
                      className={`text-xs px-2 py-1 rounded border ${
                        on
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-input'
                      }`}
                    >
                      {act}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function moduleLabel(key: string): string {
  switch (key) {
    case 'sales': return 'Ventes';
    case 'caisse': return 'Caisse';
    case 'purchases': return 'Achats';
    case 'stock': return 'Stock';
    case 'payments': return 'Paiements';
    case 'treasury': return 'Trésorerie';
    case 'checks': return 'Chèques & Traites';
    case 'withholding': return 'RAS';
    case 'storefront': return 'Storefront';
    case 'core': return 'Général';
    case 'settings': return 'Paramètres';
    default: return key;
  }
}
